import React, { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { MapPin, Airplane, MagnifyingGlass } from "@phosphor-icons/react";

export const AIRPORT = { id: "zrh-airport", name: "Flughafen Zürich (ZRH)", lat: 47.4602, lon: 8.5539, airport: true, source: "airport" };
const newToken = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Math.random()).slice(2) + Date.now());

export default function CityAutocomplete({ initial, onSelect, placeholder, accent = false, testid }) {
  const [query, setQuery] = useState(initial?.name || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const session = useRef(newToken());

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get("/places/autocomplete", { params: { q, session: session.current } });
        setResults(data.suggestions || []);
      } catch { setResults([]); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(h);
  }, [query, open]);

  const chooseAirport = () => { setQuery(AIRPORT.name); onSelect(AIRPORT); setOpen(false); setActive(-1); session.current = newToken(); };

  const choosePlace = async (s) => {
    setQuery(s.main || s.text);
    setOpen(false);
    setActive(-1);
    try {
      const { data } = await api.get("/places/details", { params: { place_id: s.place_id, session: session.current } });
      onSelect({ id: s.place_id, name: data.name || s.text, lat: data.lat, lon: data.lon, source: "google" });
    } catch { onSelect(null); }
    session.current = newToken();
  };

  const Icon = accent ? Airplane : MapPin;
  const q = query.trim().toLowerCase();
  const showAirport = q.length < 2 || "flughafen zürich zrh airport zurich".includes(q) || q === AIRPORT.name.toLowerCase();

  return (
    <div className="relative" ref={boxRef} data-testid={testid}>
      <Icon size={17} weight={accent ? "fill" : "regular"} className={`absolute left-4 top-1/2 -translate-y-1/2 ${accent ? "text-[#EF5A1E]" : "text-[#9CA0B0]"} pointer-events-none`} />
      <input
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        data-testid={testid ? `${testid}-input` : undefined}
        autoComplete="off"
        className="w-full bg-white border border-[#E9E5DE] rounded-2xl pl-11 pr-3 py-3.5 font-body text-sm text-[#0B1533] focus:border-[#EF5A1E] focus:ring-2 focus:ring-[#EF5A1E]/15 focus:outline-none transition-all duration-200 placeholder:text-[#9CA0B0]"
      />
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-2 bg-white border border-[#E9E5DE] rounded-2xl elev max-h-72 overflow-y-auto py-1.5">
          {showAirport && (
            <button type="button" onClick={chooseAirport} data-testid="city-opt-zrh-airport"
              className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-[#F5F2ED] transition-colors duration-150 border-b border-[#F0EDE6]">
              <Airplane size={16} weight="fill" className="text-[#EF5A1E] shrink-0" />
              <span className="font-body text-sm text-[#0B1533] flex-1">Flughafen Zürich (ZRH)</span>
              <span className="font-body text-xs text-[#9CA0B0]">Airport</span>
            </button>
          )}
          {loading && <div className="px-4 py-3 font-body text-sm text-[#9CA0B0] flex items-center gap-2"><MagnifyingGlass size={15} /> …</div>}
          {!loading && results.map((s, i) => (
            <button key={s.place_id || i} type="button"
              onMouseEnter={() => setActive(i)}
              onClick={() => choosePlace(s)}
              data-testid={`place-opt-${i}`}
              className={`w-full text-left px-4 py-2.5 flex items-start gap-3 transition-colors duration-150 ${active === i ? "bg-[#F5F2ED]" : ""}`}>
              <MapPin size={16} className="text-[#9CA0B0] shrink-0 mt-0.5" />
              <span className="flex-1 min-w-0">
                <span className="block font-body text-sm text-[#0B1533] truncate">{s.main}</span>
                {s.secondary && <span className="block font-body text-xs text-[#9CA0B0] truncate">{s.secondary}</span>}
              </span>
            </button>
          ))}
          {!loading && results.length === 0 && q.length >= 2 && (
            <div className="px-4 py-3 font-body text-sm text-[#9CA0B0]">Keine Adresse gefunden</div>
          )}
        </div>
      )}
    </div>
  );
}
